import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, forwardRef, Input, OnChanges, Output, ViewChild, inject } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ButtonComponent } from '../../buttons/button.component';
import { ToolTipSettings } from "../../tooltip/tooltip.util";
import { OverlayPopUpComponent } from "../../overlayed-pop-up/overlay-pop-up.component";
import { NgClass, TitleCasePipe } from "@angular/common";
import { AllIconsComponent } from "../../all-icons/all-icons.component";
import { LinkShortPipe } from "../../../../../util/pipes/link-short.pipe";
import { CheckboxComponent } from "../../checkbox/checkbox.component";
import { TextWithTooltipComponent } from "../../text-with-tooltip/text-with-tooltip.component";
import { TooltipComponent } from "../../tooltip/tooltip.component";
import { ButtonProps } from "../../buttons/button.util";
import { ScrollIntoViewDirective } from "../../../../../util/directives/scroll-into-view.directive";

@Component({
  selector: 'app-selector',
  templateUrl: './selector.component.html',
  styleUrls: ['./selector.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    multi: true,
    useExisting: forwardRef(() => SelectorComponent),
  }],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    OverlayPopUpComponent,
    NgClass,
    AllIconsComponent,
    LinkShortPipe,
    TitleCasePipe,
    CheckboxComponent,
    ButtonComponent,
    TextWithTooltipComponent,
    TooltipComponent,
    ScrollIntoViewDirective,
  ],
  standalone: true,
})
export class SelectorComponent implements OnChanges {
  private cdr = inject(ChangeDetectorRef);

  protected readonly Array = Array;

  @ViewChild('dropdown') dropdown: ElementRef;
  @Input() data: Map<any, any> | any[] = new Map<any, any>([]);
  @Input() title: string;
  @Input() selectedItem = undefined;
  @Input() selectedItemStyle: SelectedItemStyle;
  @Input() field = '';
  @Input() itemTextField: string;
  @Input() openOnClick = true;
  @Input() disabled = false;
  @Input() selectedOption = '-';
  @Input() defaultPlaceholder: string;
  @Input() titleColor: string;
  @Input() textClass: string;
  @Input() withArrow = true;
  @Input() outerClass = 'control-container'; // is the class over the whole .control class.
  /** These classes apply only for the selectors inside modals*/
  @Input() listEntryAdditionalClass = '';
  @Input() isRequired = false;
  @Input() roundDates = null;
  @Input() checkBoxField: { status: boolean; title: string }; // allows to add a checkbox field at the top of the list
  @Input() buttonField: ButtonProps; // allows to add a button at the top of the list
  /**
   * If needed the height of the list or other properties of 'overlay-pop-up' could be changed
   * by adding a new class in the array
   * [overlayContentClass] = "['overlay-pop-up', '!max-h-[12rem]]"
   */
  @Input() overlayContentClass: string | string[] = 'overlay-pop-up';
  /**
   * Controls font size, padding, and height for each list entry in the selector dropdown.
   *
   * 🔧 HTML usage:
   * <div *ngFor="let item of dataKeys; index as i"
   *      class="list-entry {{listEntryAdditionalClass}} flex flex-row justify-between items-center"
   *      [class.not-in-table]="isNotInTable">
   * </div>
   *
   * 🎨 SCSS logic (located in `overlay-pop-up.component.scss`):
   *
   * .overlay-list {
   *   ...
   *   .list-block {
   *     .list-entry {
   *       ...
   *       &.not-in-table { // IMPORTANT: YOU HAVE 2 SIZING OPTIONS: your pop-up is either inside a table(smaller) or outside of it(bigger)
   *         ...
   *       }
   */
  @Input() isNotInTable = false;
  /**
 * Prevents the overlay from repositioning.
 * When true, the overlay remains fixed in position
 */
  @Input() blockRepositioning = false;
  /**
   * Maximal length of the selected item
   */
  @Input() maxLengthSelectedItem = 30;
  @Input() showTooltip = true;
  /**
   * Value to which view must scroll, if undefined - no scrolling applied. Type must be the same as {@link field} value.
   * Note that this makes the item appear in the center if possible.
   */
  @Input() scrollIntoValue: any = undefined;
  /**
   * If true, itemEmitter and itemIndexEmitter will be called only when
   * the new value is different from the previous one.
   */
  @Input() triggerOnValueChange = false;

  @Output() itemEmitter = new EventEmitter<any>();
  @Output() itemIndexEmitter = new EventEmitter<number>();
  @Output() checkBoxFieldChange = new EventEmitter<boolean>();
  @Output() buttonClick = new EventEmitter<void>();


  showPopUpModal = false;
  isSimpleArray = true;
  dataKeys: string[] | Map<any, any> | any[] = new Map<any, any>([]);
  dataValues: string[];
  toolTipSettings: ToolTipSettings = { show: false };

  SELECTED_ITEM_ID = SELECTED_ITEM_ID;
  LIST_ITEM_ID = LIST_ITEM_ID;

  ngOnChanges(): void {
    this.isSimpleArray = this.field === '';
    if (this.data instanceof Map) {
      this.dataKeys = Array.from(this.data.keys());
      this.dataValues = Array.from(this.data.values());
    } else {
      this.dataKeys = this.data;
    }
    // Do not show 'Select option' until there is a choice: dataKeys must have at least one option to choose from.
    if (this.dataKeys?.length) {
      this.selectedOption = this.defaultPlaceholder ?? 'Select option';
    }
  }

  /**
   * Emits the value by input change => [(input)].
   * There are checks if input starts with zero or separator provides extra commas.
   * Also returns the result value.
   *
   * @param value is the raw string.
   * @param isBlurredAfterEmit for hover functionality in modals
   */
  onItemSelect(value: string, isBlurredAfterEmit = false): string {
    this.onChange(this.dataValues ? (this.data as Map<any, any>).get(value) : value);
    if (value !== undefined) {
      this.emitItem(value, isBlurredAfterEmit);
    }
    return value;
  }

  checkBoxFieldClick(event: boolean): void {
    this.checkBoxFieldChange.emit(event);
  }

  /**
   * Emits selected item.
   * In case @param isBlurredAfterEmit is true: first it blurs the dropdown-quit.
   * Then it closes dropdown completely such that it will not show again after staying with mouse on same place.
   *
   * @param item to be emitted.
   * @param isBlurredAfterEmit on "true" blurs the input.
   */
  async emitItem(item: any, isBlurredAfterEmit: boolean): Promise<void> {
    if (this.selectedItem === item && this.triggerOnValueChange) {
      return;
    }
    this.selectedItem = item;
    this.itemEmitter.emit(this.dataValues ? (this.data as Map<any, any>).get(item) : item);
    const itemIndex = Array.from(this.data.values())?.indexOf(item);
    this.itemIndexEmitter.emit(itemIndex);

    if (isBlurredAfterEmit) {
      this.dropdown.nativeElement.classList.add('blur-picker');
      await this.wait(100);
      this.dropdown.nativeElement.classList.add('turn-off');
      await this.wait(75);
      this.dropdown.nativeElement.classList.remove('turn-off');
      this.dropdown.nativeElement.classList.remove('blur-picker');
    } else {
      this.showPopUpModal = false;
      this.cdr.detectChanges(); // for some reason old selector, which is used in modals, is not closing from the first attempt
    }
  }

  onClick(): void {
    if (this.openOnClick && !this.disabled) {
      this.showPopUpModal = true;
    }
  }

  /**
   * Write form value to the DOM element (model => view)
   */
  writeValue(value: any): void {
    /**
     * When we use simple array and control accessor, it can be the case,
     * when control value is some type, but data field is an array of map values,
     * and here with the first loading we will try to set control value, which is a key of a map.
     * See modal-organization-update component with this case.
     */
    if (this.isSimpleArray && !Array.from(this.data).includes(value)) {
      return;
    }
    this.selectedItem = value;
    this.cdr.detectChanges();
  }

  /**
   * Write form disabled state to the DOM element (model => view)
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.detectChanges();
  }

  /**
   * Update form when DOM element value changes (view => model)
   */
  registerOnChange(fn: any): void {
    // Store the provided function as an internal method.
    this.onChange = fn;
  }

  /**
   * Update form when DOM element is blurred (view => model)
   */
  registerOnTouched(fn: any): void {
    // Store the provided function as an internal method.
    this.onTouched = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange(value: string): void { }

  onTouched(): void { }

  activateSelectedItemTooltip(text: string): void {
    if (text.length <= this.maxLengthSelectedItem || !this.showTooltip) {
      return;
    }
    this.toolTipSettings.title = text;
    this.toolTipSettings.coordinates = document.getElementById(SELECTED_ITEM_ID).getBoundingClientRect();
    this.toolTipSettings.show = true;
  }

  private async wait(ms: number): Promise<void> {
    await new Promise((f) => setTimeout(f, ms));
  }
}

export class SelectedItemStyle {
  text: string;
  class: string;
  constructor(text: string, classStyle: string) {
    this.text = text;
    this.class = classStyle;
  }
}

const SELECTED_ITEM_ID = 'selected-item-id';
const LIST_ITEM_ID = 'list-item-id-';

